import { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, NavLink, Navigate, useParams } from "react-router-dom";

import api from "../../services/api";
import { useUser } from "../../hook/useUser";
import ClientGroupFilter from "../../components/ui/ClientGroupFilter";
import Modal from "../../components/ui/Modal";
import PagedTable from "../../components/ui/PagedTable";
import { notify } from "../../components/ui/Toast";
import { Confirm, FormError, ModalFoot } from "../../components/ui/panelKit";
import { readApiError } from "../../utils/apiError";
import { ACTIVITY_KINDS, describeEvent } from "../../utils/auditText";
import { exactTime, relativeTime } from "../../utils/time";
import { shortAgent } from "../../utils/userAgent";

const TABS = [
  { key: "", label: "Umumiy", icon: "tabler:layout-dashboard" },
  { key: "faoliyat", label: "Faoliyat", icon: "tabler:activity" },
  { key: "kirishlar", label: "Kirishlar", icon: "tabler:login-2" },
];

const when = (t) =>
  t ? (
    <span title={exactTime(t)} style={{ whiteSpace: "nowrap" }}>
      {relativeTime(t)}
    </span>
  ) : (
    "—"
  );

/** One audit line as a table row: what, where, when, from where. */
const EventRow = ({ e, showActor = false }) => {
  const { icon, text, to } = describeEvent(e);
  return (
    <tr>
      <td className="ms-td-clamp">
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <Icon icon={icon} style={{ color: "var(--ms-muted)", flex: "0 0 auto" }} />
          {to ? (
            <Link className="ms-sublink" to={to} title={text}>
              {text}
            </Link>
          ) : (
            <span title={text}>{text}</span>
          )}
          {e.data?.backfilled && (
            <span className="ms-badge ms-badge--neutral" title="Eski yozuvlardan tiklangan">
              tarix
            </span>
          )}
        </span>
      </td>
      {showActor && <td className="ms-td-clamp">{e.actor || "—"}</td>}
      <td className="ms-td-muted">{when(e.created_at)}</td>
      <td>
        <span className="ms-mono ms-td-muted">{e.ip || "—"}</span>
      </td>
    </tr>
  );
};

/**
 * One login account, staff or cabinet (2026-10-22).
 *
 * What it did (Faoliyat, from the admin audit log), how it signed in
 * (Kirishlar), what was done to it (Hisob tarixi), and the actions on it:
 *   * Pochtani tahrirlash -- email is the only editable field; the PINFL is
 *     what OneID matches on and the name comes from OneID on every sign-in.
 *   * Sessiyalarni tugatish -- blacklists every live refresh token; an access
 *     token already out lives up to 15 minutes more unless the account is
 *     also switched off.
 *   * Mijozga oʻtkazish (staff only) and Oʻchirish / Yoqish.
 * None of them on your own account: the API refuses all of them there.
 *
 * `kind` is "staff" or "client", from the route, for the breadcrumb and the
 * parts only one of them has (staff: who created it; cabinet: its grants).
 */
const UserPage = ({ kind }) => {
  const { id, tab = "" } = useParams();
  const { user: me } = useUser();
  const isStaff = kind === "staff";
  const base = isStaff ? "/foydalanuvchilar/panel" : "/foydalanuvchilar/kabinet";
  const tabPath = (key) => `${base}/${id}${key ? `/${key}` : ""}`;

  const [state, setState] = useState({ loading: true, error: "" });
  const [account, setAccount] = useState(null);
  const [filter, setFilter] = useState({ kind: "", client: "" });
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const [email, setEmail] = useState(null);
  const [emailError, setEmailError] = useState("");

  const load = useCallback(
    () =>
      api
        .get(`/users/${id}/`)
        .then((res) => {
          setAccount(res.data);
          setState({ loading: false, error: "" });
        })
        .catch((err) =>
          setState({ loading: false, error: readApiError(err, {}, "Hisobni yuklab boʻlmadi.") }),
        ),
    [id],
  );

  useEffect(() => {
    setState({ loading: true, error: "" });
    load();
  }, [load]);

  const after = (message) => {
    notify.success(message);
    setTick((t) => t + 1);
    load();
  };

  const run = async () => {
    setBusy(true);
    try {
      if (confirm === "sessions") {
        const res = await api.post(`/users/${id}/end-sessions/`);
        after(`Sessiyalar tugatildi: ${res.data.ended} ta.`);
      } else if (confirm === "deactivate") {
        await api.patch(`/users/${id}/`, { is_active: false });
        after("Hisob oʻchirildi.");
      } else if (confirm === "demote") {
        await api.patch(`/users/${id}/`, { role: "client" });
        after("Rol oʻzgartirildi — endi kabinet hisobi.");
      }
      setConfirm(null);
    } catch (err) {
      notify.error(readApiError(err, {}, "Amalni bajarib boʻlmadi."));
    } finally {
      setBusy(false);
    }
  };

  const activate = async () => {
    try {
      await api.patch(`/users/${id}/`, { is_active: true });
      after("Hisob yoqildi.");
    } catch (err) {
      notify.error(readApiError(err, {}, "Hisobni yoqib boʻlmadi."));
    }
  };

  const saveEmail = async () => {
    setBusy(true);
    setEmailError("");
    try {
      await api.patch(`/users/${id}/`, { email: email.trim() });
      setEmail(null);
      after("Pochta saqlandi.");
    } catch (err) {
      setEmailError(readApiError(err, { email: "Elektron pochta" }, "Saqlab boʻlmadi."));
    } finally {
      setBusy(false);
    }
  };

  if (!TABS.some((t) => t.key === tab)) return <Navigate to={tabPath("")} replace />;

  if (state.loading) {
    return (
      <div className="ms-panel">
        <div className="ms-state">
          <div className="ms-spinner" />
        </div>
      </div>
    );
  }

  if (state.error || !account) {
    return (
      <div className="ms-panel">
        <div className="ms-state">
          <Icon icon="tabler:alert-triangle" />
          <span>{state.error || "Topilmadi."}</span>
        </div>
      </div>
    );
  }

  // The page follows the account, not the URL: a staff account demoted a
  // moment ago is now a cabinet one.
  if (account.role !== kind) {
    const other = account.role === "staff" ? "/foydalanuvchilar/panel" : "/foydalanuvchilar/kabinet";
    return <Navigate to={`${other}/${id}${tab ? `/${tab}` : ""}`} replace />;
  }

  const self = me && account.id === me.id;
  const name = account.full_name || account.pinfl || "—";
  const confirms = {
    sessions: {
      title: "Sessiyalarni tugatish",
      message:
        "Bu hisob hamma qurilmalarda tizimdan chiqariladi: keyingi yangilanishda qaytadan OneID orqali kirishi kerak boʻladi. Allaqachon berilgan qisqa muddatli kalit 15 daqiqagacha ishlashi mumkin — darhol toʻxtatish uchun hisobni oʻchiring.",
      label: "Tugatish",
    },
    deactivate: {
      title: "Hisobni oʻchirish",
      message: `Bu hisob ${isStaff ? "boshqaruv paneliga" : "kabinetga"} darhol kira olmaydi. Roʻyxatda va tarixda qoladi, qaytadan yoqilishi mumkin.`,
      label: "Oʻchirish",
    },
    demote: {
      title: "Mijozga oʻtkazish",
      message:
        "Bu hisob boshqaruv paneliga kira olmaydi. Kabinetga kiradi, lekin unga mijoz tizimi biriktirilmaguncha hech narsa koʻrmaydi. Orqaga qaytarib boʻlmaydi — ichki xodim uchun alohida hisob ochiladi.",
      label: "Oʻtkazish",
    },
  };
  const ct = confirm ? confirms[confirm] : null;

  const tabCount = {
    faoliyat: account.action_count,
    kirishlar: account.login_count,
  };

  return (
    <>
      <div className="ms-crumbs">
        <Link to={base}>{isStaff ? "Panel foydalanuvchilari" : "Kabinet foydalanuvchilari"}</Link>
        <Icon icon="tabler:chevron-right" />
        <span>{name}</span>
      </div>

      <div className="ms-recordhead">
        <div className="ms-recordhead-meta">
          <h1>
            {account.full_name || "Ism hali kelmagan"}
            {self && (
              <span className="ms-badge ms-badge--info" style={{ marginInlineStart: 8, verticalAlign: "middle" }}>
                siz
              </span>
            )}
          </h1>
          <span className="ms-mono">{account.pinfl || "—"}</span>
        </div>
        <span className="ms-badge ms-badge--info">{isStaff ? "Ichki xodim" : "Kabinet"}</span>
        <span className={`ms-badge ms-badge--${account.is_active ? "success" : "danger"}`}>
          {account.is_active ? "Faol" : "Oʻchirilgan"}
        </span>
        <div className="ms-toolbar-spacer" />
        <button
          type="button"
          className="ms-btn ms-btn--ghost ms-btn--sm"
          onClick={() => {
            setEmail(account.email || "");
            setEmailError("");
          }}
        >
          <Icon icon="tabler:mail" />
          Pochtani tahrirlash
        </button>
        <button
          type="button"
          className="ms-btn ms-btn--ghost ms-btn--sm"
          disabled={self || !account.session_count}
          title={self ? "Oʻz sessiyalaringizni bu yerdan tugata olmaysiz" : account.session_count ? "" : "Faol sessiya yoʻq"}
          onClick={() => setConfirm("sessions")}
        >
          <Icon icon="tabler:logout" />
          Sessiyalarni tugatish
        </button>
        {isStaff && !self && (
          <button type="button" className="ms-btn ms-btn--ghost ms-btn--sm" onClick={() => setConfirm("demote")}>
            <Icon icon="tabler:arrow-down" />
            Mijozga oʻtkazish
          </button>
        )}
        {!self &&
          (account.is_active ? (
            <button type="button" className="ms-btn ms-btn--ghost ms-btn--sm" onClick={() => setConfirm("deactivate")}>
              <Icon icon="tabler:user-off" />
              Oʻchirish
            </button>
          ) : (
            <button type="button" className="ms-btn ms-btn--primary ms-btn--sm" onClick={activate}>
              <Icon icon="tabler:user-check" />
              Yoqish
            </button>
          ))}
      </div>

      {!isStaff && account.is_active && !account.clients?.length && (
        <div className="ms-alertbar" role="status">
          <div>
            <Icon icon="tabler:lock-off" />
            <span>
              Bu kabinet hisobiga hech qanday mijoz tizimi biriktirilmagan — kirsa ham hech narsa
              koʻrmaydi. Ruxsat mijoz tizimining Menejerlar boʻlimida beriladi.
            </span>
          </div>
        </div>
      )}

      <nav className="ms-tabs" aria-label="Boʻlimlar">
        {TABS.map((t) => (
          <NavLink
            key={t.key}
            to={tabPath(t.key)}
            end
            className={() => `ms-tab${tab === t.key ? " is-active" : ""}`}
          >
            <Icon icon={t.icon} />
            {t.label}
            {tabCount[t.key] !== undefined && <span className="ms-tab-count">{tabCount[t.key]}</span>}
          </NavLink>
        ))}
      </nav>

      {tab === "" && (
        <>
          <div className="ms-stats">
            <Link className="ms-stat" to={tabPath("faoliyat")}>
              <div className="ms-stat-icon">
                <Icon icon="tabler:activity" />
              </div>
              <div className="ms-stat-body">
                <span>Amallar</span>
                <b>{account.action_count}</b>
              </div>
            </Link>
            <Link className="ms-stat" to={tabPath("kirishlar")}>
              <div className="ms-stat-icon">
                <Icon icon="tabler:login-2" />
              </div>
              <div className="ms-stat-body">
                <span>Kirishlar</span>
                <b>{account.login_count}</b>
              </div>
            </Link>
            <div className="ms-stat">
              <div className="ms-stat-icon">
                <Icon icon="tabler:devices" />
              </div>
              <div className="ms-stat-body">
                <span>Faol sessiyalar</span>
                <b>{account.session_count}</b>
              </div>
            </div>
            {!isStaff && (
              <div className="ms-stat">
                <div className={`ms-stat-icon${account.clients?.length ? "" : " is-warn"}`}>
                  <Icon icon="tabler:server-2" />
                </div>
                <div className="ms-stat-body">
                  <span>Mijoz tizimlari</span>
                  <b>{account.clients?.length || 0}</b>
                </div>
              </div>
            )}
          </div>

          <div className="ms-recordgrid">
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div className="ms-panel">
                <div className="ms-panel-head">
                  <Icon icon="tabler:id" />
                  <h2>Maʼlumot</h2>
                </div>
                <dl className="ms-facts">
                  <div>
                    <dt>Toʻliq ism</dt>
                    <dd>{account.full_name || "—"}</dd>
                  </div>
                  <div>
                    <dt>PINFL</dt>
                    <dd className="ms-mono">{account.pinfl || "—"}</dd>
                  </div>
                  <div>
                    <dt>Elektron pochta</dt>
                    <dd>{account.email || "—"}</dd>
                  </div>
                  <div>
                    <dt>Qoʻshgan</dt>
                    <dd>{account.created_by || "—"}</dd>
                  </div>
                  <div>
                    <dt>Hisob ochilgan</dt>
                    <dd>{when(account.created_at)}</dd>
                  </div>
                  <div>
                    <dt>Oxirgi kirish (OneID)</dt>
                    <dd>{account.last_oneid_login_at ? when(account.last_oneid_login_at) : "hali kirmagan"}</dd>
                  </div>
                  <div>
                    <dt>Oxirgi amal</dt>
                    <dd>{when(account.last_action_at)}</dd>
                  </div>
                </dl>
              </div>

              {!isStaff && (
                <div className="ms-panel">
                  <div className="ms-panel-head">
                    <Icon icon="tabler:server-2" />
                    <h2>Mijoz tizimlari</h2>
                  </div>
                  {account.clients?.length ? (
                    <dl className="ms-facts">
                      {account.clients.map((c) => (
                        <div key={c.id}>
                          <dt>
                            <Link className="ms-sublink" to={`/mijoz-tizimlari/${c.id}/menejerlar`}>
                              {c.name}
                            </Link>
                          </dt>
                          <dd>
                            <span className="ms-badge ms-badge--neutral">
                              {c.access === "admin" ? "administrator" : "oʻqish"}
                            </span>
                          </dd>
                        </div>
                      ))}
                    </dl>
                  ) : (
                    <div className="ms-state">
                      <Icon icon="tabler:lock-off" />
                      <span>Ruxsat yoʻq.</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="ms-panel">
              <div className="ms-panel-head">
                <Icon icon="tabler:history" />
                <h2>Hisob tarixi</h2>
                <div className="ms-toolbar-spacer" />
                <span className="ms-td-muted" style={{ fontSize: 12.5 }}>
                  Bu hisobga nisbatan qilingan amallar
                </span>
              </div>
              <PagedTable
                path={`/users/${id}/history/`}
                params={{}}
                reloadKey={tick}
                emptyIcon="tabler:history-off"
                empty="Hali yozuv yoʻq (tarix 22-oktabrdan boshlab yoziladi)."
                head={
                  <tr>
                    <th>Amal</th>
                    <th style={{ width: 160 }}>Kim</th>
                    <th style={{ width: 120 }}>Qachon</th>
                    <th style={{ width: 120 }}>IP</th>
                  </tr>
                }
                row={(e) => <EventRow key={e.id} e={e} showActor />}
              />
            </div>
          </div>
        </>
      )}

      {tab === "faoliyat" && (
        <div className="ms-panel">
          <div className="ms-toolbar">
            <select
              className="ms-select ms-select--sm"
              value={filter.kind}
              aria-label="Amal turi"
              onChange={(e) => setFilter((f) => ({ ...f, kind: e.target.value }))}
            >
              {ACTIVITY_KINDS.map(([v, label]) => (
                <option key={v} value={v}>
                  {label}
                </option>
              ))}
            </select>
            <ClientGroupFilter
              client={filter.client}
              group=""
              withGroups={false}
              onChange={({ client }) => setFilter((f) => ({ ...f, client }))}
            />
            {(filter.kind || filter.client) && (
              <button type="button" className="ms-btn ms-btn--ghost" onClick={() => setFilter({ kind: "", client: "" })}>
                <Icon icon="tabler:x" />
                Tozalash
              </button>
            )}
            <div className="ms-toolbar-spacer" />
            <span className="ms-td-muted" style={{ fontSize: 12.5 }}>
              Panel va kabinetdagi amallar (22-oktabrdan, avvalgilari «tarix»)
            </span>
          </div>
          <PagedTable
            path={`/users/${id}/activity/`}
            params={{
              ...(filter.kind ? { kind: filter.kind } : {}),
              ...(filter.client ? { client: filter.client } : {}),
            }}
            reloadKey={tick}
            emptyIcon="tabler:activity"
            empty={filter.kind || filter.client ? "Hech narsa topilmadi." : "Bu hisob hali hech qanday amal bajarmagan."}
            head={
              <tr>
                <th>Amal</th>
                <th style={{ width: 130 }}>Qachon</th>
                <th style={{ width: 130 }}>IP</th>
              </tr>
            }
            row={(e) => <EventRow key={e.id} e={e} />}
          />
        </div>
      )}

      {tab === "kirishlar" && (
        <div className="ms-panel">
          <div className="ms-panel-head">
            <Icon icon="tabler:login-2" />
            <h2>Tizimga kirishlar</h2>
            <div className="ms-toolbar-spacer" />
            <span className="ms-td-muted" style={{ fontSize: 12.5 }}>
              22-oktabrdan boshlab yoziladi
            </span>
          </div>
          <PagedTable
            path={`/users/${id}/logins/`}
            params={{}}
            reloadKey={tick}
            emptyIcon="tabler:login-2"
            empty="Hali kirish yozilmagan."
            head={
              <tr>
                <th style={{ width: 160 }}>Vaqt</th>
                <th>Usul</th>
                <th style={{ width: 150 }}>Qayerga</th>
                <th style={{ width: 140 }}>IP</th>
                <th style={{ width: 200 }}>Brauzer</th>
              </tr>
            }
            row={(e) => (
              <tr key={e.id}>
                <td className="ms-td-muted">{when(e.created_at)}</td>
                <td>
                  {e.data?.method === "hidden" ? (
                    <span className="ms-badge ms-badge--progress">yashirin kirish (qurilma)</span>
                  ) : (
                    <span className="ms-badge ms-badge--neutral">OneID</span>
                  )}
                </td>
                <td>{e.data?.panel === "kabinet" ? "Kabinet" : "Boshqaruv paneli"}</td>
                <td>
                  <span className="ms-mono">{e.ip || "—"}</span>
                </td>
                <td className="ms-td-muted" title={e.user_agent}>
                  {shortAgent(e.user_agent)}
                </td>
              </tr>
            )}
          />
        </div>
      )}

      <Modal
        open={email !== null}
        title="Elektron pochta"
        onClose={() => setEmail(null)}
        footer={<ModalFoot onCancel={() => setEmail(null)} onSave={saveEmail} saving={busy} saveLabel="Saqlash" />}
      >
        {email !== null && (
          <>
            <FormError>{emailError}</FormError>
            <div className="ms-field">
              <label htmlFor="acc-email">Elektron pochta</label>
              <input
                id="acc-email"
                type="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <span className="ms-field-hint">
                Faqat roʻyxatda tanish uchun belgi — OneID pochta qaytarmaydi. Boʻsh qoldirsangiz
                oʻchiriladi. Ism va PINFL tahrirlanmaydi: ism har kirishda OneID dan yangilanadi.
              </span>
            </div>
          </>
        )}
      </Modal>

      <Confirm
        open={confirm !== null}
        busy={busy}
        name={name}
        title={ct?.title}
        message={ct?.message}
        confirmLabel={ct?.label}
        cancelLabel="Bekor qilish"
        busyLabel="Bajarilmoqda..."
        onConfirm={run}
        onCancel={() => setConfirm(null)}
      />
    </>
  );
};

export default UserPage;
